import { getLocaleString, getCountryCode } from 'utilities/locales';
import { getEnvironment } from 'utilities/environment';
import { VATStores } from 'constants/store_constants';
import Dinero from 'dinero.js';

// Taken from ISO 4217. https://www.currency-iso.org/dam/downloads/lists/list_one.xml
// Values represent number of decimal digits difference between major and minor units.
const CURRENCY_OPTIONS = {
  default: {
    precision: 2,
    format: '$0,0.00',
  },
  JPY: {
    precision: 0,
    format: '$0,0',
  },
};

const getNumberFormat = ({ locale = getLocaleString(), currency }) => {
  const options = {
    style: 'currency',
    currency,
    ...(currency === 'CAD' && { currencyDisplay: 'narrowSymbol' }),
  };

  // Some older versions of Safari don't support currencyDisplay: narrowSymbol
  // so we need a fallback here
  try {
    return Intl.NumberFormat(locale, options);
  } catch {
    return Intl.NumberFormat(locale, { ...options, currencyDisplay: 'symbol' });
  }
};

const currencySymbol = (currency = getEnvironment().currency_code) => {
  const formatParts = getNumberFormat({ currency }).formatToParts();
  return formatParts.find((part) => part.type === 'currency').value;
};

const toCurrency = (number, currency = getEnvironment().currency_code) => {
  return getNumberFormat({ currency }).format(number);
};

const toMinorCurrencyUnit = (
  number,
  currency = getEnvironment().currency_code,
) => {
  const { precision } = CURRENCY_OPTIONS[currency] || CURRENCY_OPTIONS.default;
  const factor = Math.pow(10, precision);
  return Math.round(factor * number);
};

const toMinorCurrencyUnitWithSymbol = (
  number,
  currency = getEnvironment().currency_code,
) => {
  const { precision } = CURRENCY_OPTIONS[currency] || CURRENCY_OPTIONS.default;
  const factor = Math.pow(10, precision);

  return Dinero({
    amount: Math.round(factor * number),
    currency,
    precision,
  })
    .setLocale(getLocaleString())
    .toFormat('$0');
};

const toCurrencyWithoutSymbol = (
  number,
  locale = getLocaleString(),
  currency = getEnvironment().currency_code,
) => {
  const resolvedOptions = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).resolvedOptions();

  return number.toLocaleString(locale, {
    minimumFractionDigits: resolvedOptions.minimumFractionDigits,
    maximumFractionDigits: resolvedOptions.maximumFractionDigits,
  });
};

// Will create currency string using the minor currency unit
const toCurrencyWithMinorUnitAmount = (
  minorUnitAmount,
  currency = getEnvironment().currency_code,
) => {
  const { precision, format } =
    CURRENCY_OPTIONS[currency] || CURRENCY_OPTIONS.default;

  return Dinero({
    amount: minorUnitAmount,
    currency,
    precision,
  })
    .setLocale(getLocaleString())
    .toFormat(format);
};

// Dinero may be overkill for simply converting from float to formatted currency,
// but it is handy for performing arithmetic operations on currency.
//
// [param] amount: may be a number or currency string
const toDinero = (amount, currency = getEnvironment().currency_code) => {
  const { precision } = CURRENCY_OPTIONS[currency] || CURRENCY_OPTIONS.default;

  return Dinero({
    amount: toMinorCurrencyUnit(amount),
    currency,
    precision,
  }).setLocale(getLocaleString());
};

const displayVAT = () => {
  return VATStores.includes(getCountryCode().toUpperCase());
};

export {
  toMinorCurrencyUnit,
  currencySymbol,
  toCurrency,
  toCurrencyWithoutSymbol,
  toDinero,
  toCurrencyWithMinorUnitAmount,
  toMinorCurrencyUnitWithSymbol,
  displayVAT,
};
